import React, { useRef, useState } from 'react';
import { toPng } from 'html-to-image';
import { Copy, Download, Share2, Check, TrendingUp, ExternalLink } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';

const ShareCard = ({ result }) => {
  const cardRef = useRef(null);
  const [copySuccess, setCopySuccess] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const generateSummaryText = () => {
    return `🥇 If my money was gold…

Invested: ${result.currency.symbol}${result.investmentAmount.toLocaleString()} ${result.currency.code}
Gold today: ${result.gramsOfGold} grams
Scenario: ${result.scenario.label} (${result.annualRate}%/year)
Horizon: ${result.horizonLabel}
Estimated value: ${result.currency.symbol}${parseFloat(result.futureValueSelected).toLocaleString()}

Calculated at goldcalc.app`;
  };

  const handleCopyText = async () => {
    try {
      await navigator.clipboard.writeText(generateSummaryText());
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleDownloadPNG = async () => {
    if (!cardRef.current) return;
    
    setDownloading(true);
    try {
      const dataUrl = await toPng(cardRef.current, {
        quality: 1.0,
        pixelRatio: 2,
        backgroundColor: '#12121a',
      });
      
      const link = document.createElement('a');
      link.download = `gold-investment-${Date.now()}.png`;
      link.href = dataUrl;
      link.click();
    } catch (err) {
      console.error('Failed to generate image:', err);
    } finally {
      setDownloading(false);
    }
  };

  const handleShare = async () => {
    const shareData = {
      title: 'My Gold Investment Calculation',
      text: generateSummaryText(),
      url: window.location.href,
    };

    if (navigator.share && navigator.canShare(shareData)) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        if (err.name !== 'AbortError') {
          handleCopyText();
        }
      }
    } else {
      handleCopyText();
    }
  };

  return (
    <Card className="bg-[#12121a] border-[#1e1e2e] rounded-2xl overflow-hidden">
      <CardHeader className="pb-2 border-b border-[#1e1e2e]">
        <CardTitle className="flex items-center gap-2 text-lg font-semibold text-white">
          <Share2 className="w-5 h-5 text-[#D4AF37]" />
          Share Your Results
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 p-6">
        {/* Shareable Card Preview */}
        <div
          ref={cardRef}
          className="p-6 bg-gradient-to-br from-[#12121a] via-[#1a1a25] to-[#12121a] rounded-xl border border-[#D4AF37]/20"
        >
          {/* Card Header */}
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#D4AF37] to-[#B8960C] flex items-center justify-center">
              <TrendingUp className="w-4 h-4 text-black" />
            </div>
            <span className="font-semibold text-white">If my money was gold…</span>
          </div>

          {/* Investment Details */}
          <div className="space-y-3 mb-4">
            {[
              { label: 'Invested', value: `${result.currency.symbol}${result.investmentAmount.toLocaleString()} ${result.currency.code}` },
              { label: 'Gold today', value: `${result.gramsOfGold} grams` },
              { label: 'Scenario', value: `${result.scenario.label} (${result.annualRate}%/year)` },
              { label: 'Horizon', value: result.horizonLabel }
            ].map((item, index) => (
              <div key={index} className="flex justify-between items-center py-2 border-b border-[#D4AF37]/10">
                <span className="text-sm text-gray-400">{item.label}</span>
                <span className="font-semibold text-white">{item.value}</span>
              </div>
            ))}
          </div>

          {/* Future Value Highlight */}
          <div className="p-4 bg-[#0a0a0f] rounded-lg border border-[#D4AF37]/20 mb-4">
            <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Estimated Future Value</p>
            <p className="text-2xl font-bold text-[#D4AF37]">
              {result.currency.symbol}{parseFloat(result.futureValueSelected).toLocaleString()}
            </p>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between text-xs text-gray-500">
            <span>Powered by Emergent</span>
            <span>goldcalc.app</span>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            onClick={handleCopyText}
            className="flex-1 h-11 bg-transparent border border-[#1e1e2e] text-gray-300 hover:bg-[#1e1e2e] hover:text-white rounded-xl transition-all gap-2"
          >
            {copySuccess ? (
              <Check className="w-4 h-4 text-emerald-400" />
            ) : (
              <Copy className="w-4 h-4" />
            )}
            {copySuccess ? 'Copied!' : 'Copy Summary'}
          </Button>
          <Button
            onClick={handleDownloadPNG}
            disabled={downloading}
            className="flex-1 h-11 bg-transparent border border-[#1e1e2e] text-gray-300 hover:bg-[#1e1e2e] hover:text-white rounded-xl transition-all gap-2"
          >
            <Download className="w-4 h-4" />
            {downloading ? 'Generating...' : 'Download PNG'}
          </Button>
          <Button
            onClick={handleShare}
            className="flex-1 h-11 btn-gold text-black rounded-xl gap-2"
          >
            <Share2 className="w-4 h-4" />
            Share
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default ShareCard;
